/**
 * A dictated transcript as one row per speaker turn, and the round-trip between
 * those rows and the single block of text the note is stored as. Pure and
 * Angular-free so the rules can be tested directly.
 *
 * Recognition attributes a turn only once it has finished, so a turn still being
 * spoken has no speaker and is held separately as the live row. Rows the rep did
 * not want — someone else in the room, a passing conversation — are deleted here
 * rather than guessed at during recognition.
 */

/** Recognition's label for an unfinished turn; never a real speaker. */
export const UNKNOWN_SPEAKER = 'Unknown';

/** The label a turn falls back to when recognition gave none. */
export const DEFAULT_SPEAKER = 'Speaker 1';

/** `Speaker 1: said something` — how a turn is written into the stored note. */
const LINE = /^\s*((?:Guest|Speaker)[\s-]?\d+)\s*:\s*(.*)$/i;

/**
 * Recognition numbers its voices `Guest-1`, `Guest-2`. The rep is being shown who
 * spoke, not the vendor's word for it, so every machine label is read as a
 * speaker number; a name the rep typed is theirs and is left exactly as written.
 */
const MACHINE_LABEL = /^(?:Guest|Speaker)[\s-]?(\d+)$/i;

function asSpeakerLabel(name: string): string {
  const numbered = MACHINE_LABEL.exec(name);
  return numbered ? `Speaker ${numbered[1]}` : name;
}

export interface TranscriptSegment {
  /** Stable across appends, so editing one row is not disturbed by later ones. */
  id: number;
  speaker: string;
  text: string;
  /**
   * A turn recognition may not carry on: one the rep typed themselves, or a
   * placeholder. Speech that follows it is the next thing said, not more of what
   * came before.
   */
  closed?: boolean;
  /**
   * A placeholder for words recorded while live recognition was out of reach, which
   * holds their place until they are read. It holds no text, is never shown, and
   * never reaches the note.
   */
  pending?: boolean;
}

let nextId = 1;

export function makeSegment(speaker: string | null | undefined, text: string): TranscriptSegment {
  const named = (speaker ?? '').trim();
  return {
    id: nextId++,
    speaker: !named || named === UNKNOWN_SPEAKER ? DEFAULT_SPEAKER : asSpeakerLabel(named),
    text: text.trim(),
  };
}

/**
 * Read a stored note back into rows. A note written as plain prose becomes a
 * single turn, so notes taken before speakers were separated still open here.
 * A line carrying a speaker keeps it.
 */
export function parseTranscript(stored: string | null | undefined): TranscriptSegment[] {
  const raw = (stored ?? '').trim();
  if (!raw) return [];

  if (!isLabelled(raw)) {
    return [makeSegment(DEFAULT_SPEAKER, raw)];
  }

  const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
  const out: TranscriptSegment[] = [];
  for (const line of lines) {
    const [, speaker, text] = LINE.exec(line) ?? [];
    if (!text?.trim()) continue;
    const previous = out[out.length - 1];
    // Recognition can split one turn across results; keep them as one row.
    if (previous && previous.speaker === asSpeakerLabel(speaker)) {
      previous.text = `${previous.text} ${text.trim()}`.trim();
      continue;
    }
    out.push(makeSegment(speaker, text));
  }
  return out;
}

/**
 * Whether a stored note is written as turns, one speaker to a line. It is only when
 * every line carries a speaker, so a stray colon in prose does not split a paragraph
 * into turns.
 */
function isLabelled(stored: string): boolean {
  const lines = stored.split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.length > 0 && lines.every((l) => LINE.test(l));
}

/**
 * Write the rows back to the note. A note from one speaker is stored as plain
 * prose, so nothing changes for the ordinary case of a rep dictating alone;
 * more than one speaker keeps the labels so the turns survive a reopen.
 */
export function serializeTranscript(segments: TranscriptSegment[]): string {
  const kept = (segments ?? []).filter((s) => s.text.trim());
  if (!kept.length) return '';
  const speakers = new Set(kept.map((s) => s.speaker));
  if (speakers.size <= 1) {
    return kept.map((s) => s.text.trim()).join(' ');
  }
  return kept.map((s) => `${s.speaker}: ${s.text.trim()}`).join('\n');
}

/**
 * Add a finished turn. Consecutive turns from one speaker join the row already
 * open for them, so a continuous stretch of speech reads as one paragraph
 * rather than one row per breath — unless that row was closed by the rep typing
 * it, which the next thing spoken is not a continuation of.
 */
export function appendSegment(
  segments: TranscriptSegment[],
  speaker: string | null | undefined,
  text: string,
): TranscriptSegment[] {
  const said = text.trim();
  if (!said) return segments;
  const next = [...segments];
  const last = next[next.length - 1];
  const named = makeSegment(speaker, said);
  if (last && !last.closed && last.speaker === named.speaker) {
    next[next.length - 1] = { ...last, text: `${last.text} ${said}`.trim() };
    return next;
  }
  next.push(named);
  return next;
}

export function removeSegment(segments: TranscriptSegment[], id: number): TranscriptSegment[] {
  return (segments ?? []).filter((s) => s.id !== id);
}

export function editSegment(segments: TranscriptSegment[], id: number, text: string): TranscriptSegment[] {
  return (segments ?? []).map((s) => (s.id === id ? { ...s, text } : s));
}

/**
 * Rename who was speaking. Recognition can only tell one voice from another; the
 * rep knows it was Dr Nair. An emptied name falls back rather than leaving a
 * turn belonging to nobody.
 */
export function editSpeaker(segments: TranscriptSegment[], id: number, speaker: string): TranscriptSegment[] {
  const named = speaker.trim() || DEFAULT_SPEAKER;
  return (segments ?? []).map((s) => (s.id === id ? { ...s, speaker: named } : s));
}

/** Whether anything would reach the note. */
export function hasContent(segments: TranscriptSegment[]): boolean {
  return (segments ?? []).some((s) => s.text.trim().length > 0);
}

/** A placeholder row for words to be read later; see {@link TranscriptSegment.pending}. */
export function makePlaceholder(): TranscriptSegment {
  return { ...makeSegment(DEFAULT_SPEAKER, ''), closed: true, pending: true };
}

/** The rows with the row `id` replaced by `rows`, or unchanged where there is no such row. */
export function replaceSegment(
  segments: TranscriptSegment[],
  id: number,
  rows: TranscriptSegment[],
): TranscriptSegment[] {
  const at = segments.findIndex((segment) => segment.id === id);
  return at === -1 ? segments : [...segments.slice(0, at), ...rows, ...segments.slice(at + 1)];
}

/** Text no transcript holds, standing in for a row while the note is written out around it. */
const MARKER = '\uE000';

/**
 * The note as stored, up to the row `id`: the prefix of `serializeTranscript` that
 * comes before where that row stands, whichever form, prose or labelled turns, the
 * note is stored in. Null where there is no such row.
 */
export function transcriptBefore(segments: TranscriptSegment[], id: number): string | null {
  const at = segments.findIndex((segment) => segment.id === id);
  if (at === -1) {
    return null;
  }
  const speaker = segments.find((segment) => segment.id !== id && segment.text.trim())?.speaker ?? DEFAULT_SPEAKER;
  const marked = segments.map((segment) => (segment.id === id ? { ...segment, speaker, text: MARKER } : segment));
  const written = serializeTranscript(marked);
  const cut = written.indexOf(MARKER);
  const lineStart = written.lastIndexOf('\n', cut) + 1;
  const labelled = written.slice(lineStart, cut) === `${speaker}: `;
  return written.slice(0, labelled ? lineStart : cut).trimEnd();
}

/** Words read from a recording, and the stored note text they follow, or null to add them at the end. */
export interface ReadWords {
  words: string;
  before: string | null;
}

/**
 * The stored note with words read from its recording put in place: each after the
 * text it followed when the note was saved, where the note still begins with that
 * text, and otherwise, like words with no place, at the end. Words that follow the
 * same text keep the order of `readings`.
 *
 * The words are written in the note's own form, so it still reads back the same
 * way: a note written as turns gets one labelled line per turn read, prose gets the
 * words without labels, each set apart by a blank line. A note with no text takes
 * turns where any reading holds more than one speaker.
 */
export function placeReadWords(existing: string, readings: ReadWords[]): string {
  const said = readings.filter((reading) => reading.words.trim());
  const labelled = existing.trim() ? isLabelled(existing) : said.some((reading) => isLabelled(reading.words));
  const separator = labelled ? '\n' : '\n\n';
  const placed = said.map((reading, order) => ({
    words: inNoteForm(reading.words, labelled),
    order,
    at: reading.before !== null && existing.startsWith(reading.before) ? reading.before.length : -1,
  }));
  let text = existing;
  for (const reading of placed.filter((r) => r.at !== -1).sort((a, b) => b.at - a.at || b.order - a.order)) {
    text = joinText(separator, text.slice(0, reading.at), reading.words, text.slice(reading.at));
  }
  for (const reading of placed.filter((r) => r.at === -1)) {
    text = joinText(separator, text, reading.words);
  }
  return text;
}

/** Read words as a note written as turns (`labelled`) or as prose holds them. */
function inNoteForm(words: string, labelled: boolean): string {
  const turns = parseTranscript(words);
  return labelled
    ? turns.map((turn) => `${turn.speaker}: ${turn.text}`).join('\n')
    : turns.map((turn) => turn.text).join(' ');
}

function joinText(separator: string, ...pieces: string[]): string {
  return pieces.map((piece) => piece.trim()).filter(Boolean).join(separator);
}
